/* eslint-disable react/no-array-index-key */
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  makeStyles
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import bytesToSize from 'src/utils/bytesToSize';
import { X } from 'react-feather';
import Button from './Button';
import Loader from './Loader';
import DocumentService from '../utils/documentService';

const useStyles = makeStyles((theme) => ({
  root: {},
  dropZone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    outline: 'none',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      opacity: 0.5,
      cursor: 'pointer'
    }
  },
  dragActive: {
    backgroundColor: theme.palette.action.active,
    opacity: 0.5
  },
  image: {
    width: 40,
    marginRight: theme.spacing(2)
  },
  info: {
    marginTop: theme.spacing(1)
  },
  list: {
    maxHeight: 320
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > * + *': {
      marginLeft: theme.spacing(2)
    }
  }
}));

type Props = {
  filesUploading: File[],
  documents: Document[],
  handleDrop: any,
  handleRemove: any,
  multiple?: boolean,
  className?: string,
};

function FilesDropzone({
  className,
  filesUploading,
  documents,
  handleDrop,
  handleRemove,
  multiple,
}: Props) {
  const classes = useStyles();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple,
  });

  return (
    <div
      className={clsx(classes.root, className)}
    >
      <div
        className={clsx({
          [classes.dropZone]: true,
          [classes.dragActive]: isDragActive
        })}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Box display="flex" alignItems="center">
          <img
            alt="Select file"
            className={classes.image}
            src="/static/images/undraw_add_file2_gvbb.svg"
          />
          <Typography
            color="textPrimary"
            variant="body1"
          >
            Drag and drop or browse files
          </Typography>
        </Box>
      </div>
      {filesUploading.length > 0 && (
        <>
          <PerfectScrollbar options={{ suppressScrollX: true }}>
            <List className={classes.list}>
              {filesUploading.map((file: any, i: any) => (
                <ListItem
                  divider={i < filesUploading.length - 1}
                  key={i}
                >
                  <Box display="flex" justifyContent="space-between" flex="1 1">
                    <Box display="flex" alignItems="center">
                      <ListItemIcon>
                        <FileCopyIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        primaryTypographyProps={{ variant: 'h5' }}
                        secondary={bytesToSize(file.size)}
                      />
                    </Box>
                    <Box>
                      <Loader />
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </PerfectScrollbar>
        </>
      )}
      {documents.length > 0 && (
        <>
          <PerfectScrollbar options={{ suppressScrollX: true }}>
            <List className={classes.list}>
              {documents.map((document: any, i: any) => (
                <ListItem
                  divider={i < documents.length - 1}
                  key={i}
                >
                  <ListItemIcon>
                    <FileCopyIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={document.fileName}
                    primaryTypographyProps={{ variant: 'h5' }}
                    secondary={bytesToSize(document.fileSize)}
                  />
                  <IconButton edge="end" onClick={() => handleRemove(document.id)}>
                    <X />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </PerfectScrollbar>
        </>
      )}
    </div>
  );
}

export default FilesDropzone;
